/**
 * AppSidebar — fixed-left icon nav rail for tablet breakpoint (#704 PR-5).
 *
 * Renders only when useBreakpoint() === 'tablet'. Phone uses BottomTabBar;
 * desktop uses the top Navbar. AppSidebar holds the same 5 primary nav
 * items as BottomTabBar (Map / Picks / Saved / Alerts / More) so the
 * cross-breakpoint mental model stays consistent.
 *
 * Layout: 64px icon-only rail pinned to the left edge of the viewport.
 * Width matches the iPad Files / Mail sidebar collapsed state — narrow
 * enough that the existing two-column `.search-page` + ParcelDetail
 * `.detail-grid` both still get room to breathe at 768px.
 *
 * Active-tab detection uses react-router's useLocation and prefix-matches
 * the route family so /opportunities/anything still highlights "Picks".
 * "More" is active whenever the current path is NOT claimed by another
 * primary tab — mirrors BottomTabBar's behavior so the MoreSheet feels
 * consistent across breakpoints.
 *
 * Badge sources are passed in as props so this component stays purely
 * presentational and the parent (App.js MobileShell → TabletShell)
 * controls polling / caching.
 *
 * Per the PR-5 brief: at tablet, BottomTabBar hides; AppSidebar takes
 * over. CSS in App.css does the cross-breakpoint show/hide and pushes
 * .main-content right by the rail width.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

// Inline SVGs — same set as BottomTabBar so the visual identity is
// consistent across breakpoints. Kept inline to avoid an icon-library
// dependency and to keep the bundle slim.
function IconMap() {
  return (
    <svg
      width="22" height="22" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconStar() {
  return (
    <svg
      width="22" height="22" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function IconBookmark() {
  return (
    <svg
      width="22" height="22" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconBell() {
  return (
    <svg
      width="22" height="22" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function IconMore() {
  return (
    <svg
      width="22" height="22" viewBox="0 0 24 24"
      fill="currentColor" stroke="none"
      aria-hidden="true"
    >
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}

// Same 5 tabs as BottomTabBar. The labels show as titles + sr-only text
// rather than visible captions because the rail is icon-only at 64px.
const TABS = [
  { key: 'map',    label: 'Map',    path: '/',              Icon: IconMap },
  { key: 'picks',  label: 'Picks',  path: '/opportunities', Icon: IconStar },
  { key: 'saved',  label: 'Saved',  path: '/saved',         Icon: IconBookmark, badgeFrom: 'saved' },
  { key: 'alerts', label: 'Alerts', path: '/watchlists',    Icon: IconBell,     badgeFrom: 'alerts' },
  { key: 'more',   label: 'More',                            Icon: IconMore,     isMore: true },
];

// True if `pathname` is on (or under) `tabPath`. Treats "/" specially
// so that /parcel/123 doesn't keep the Map tab lit when the user
// navigates deeper — Map is only active on the exact root.
function isPathMatch(pathname, tabPath) {
  if (tabPath === '/') return pathname === '/';
  return pathname === tabPath || pathname.startsWith(tabPath + '/');
}

function AppSidebar({ savedCount = 0, alertsCount = 0, onMoreClick }) {
  const location = useLocation();
  const pathname = location.pathname;

  const primaryTabs = TABS.filter((t) => !t.isMore);
  const activePrimary = primaryTabs.find((t) => isPathMatch(pathname, t.path));
  const moreActive = !activePrimary;

  const badgeFor = (key) => {
    if (key === 'saved') return savedCount;
    if (key === 'alerts') return alertsCount;
    return 0;
  };

  return (
    <nav
      className="app-sidebar"
      role="navigation"
      aria-label="Primary tablet navigation"
      data-testid="app-sidebar"
    >
      {TABS.map((tab) => {
        const { Icon } = tab;
        const isActive = tab.isMore
          ? moreActive
          : activePrimary && activePrimary.key === tab.key;
        const count = tab.badgeFrom ? badgeFor(tab.badgeFrom) : 0;
        const showBadge = count > 0;
        const badgeText = count > 999 ? '999+' : String(count);

        const inner = (
          <>
            <span className="app-sidebar-icon" aria-hidden="true">
              <Icon />
              {showBadge && (
                <span
                  className="app-sidebar-badge"
                  data-testid={`app-sidebar-badge-${tab.key}`}
                >
                  {badgeText}
                </span>
              )}
            </span>
            {/* sr-only label so screen readers + tests can find each
                tab by its visible name even though the rail is icon-only. */}
            <span className="app-sidebar-label">{tab.label}</span>
          </>
        );

        const classes = `app-sidebar-item ${isActive ? 'app-sidebar-item--active' : ''}`;
        const ariaProps = {
          'aria-current': isActive ? 'page' : undefined,
          'aria-label':
            tab.label + (showBadge ? `, ${count} unread` : ''),
          title: tab.label,
        };

        if (tab.isMore) {
          return (
            <button
              key={tab.key}
              type="button"
              className={classes}
              onClick={onMoreClick}
              data-testid={`app-sidebar-${tab.key}`}
              {...ariaProps}
            >
              {inner}
            </button>
          );
        }

        return (
          <Link
            key={tab.key}
            to={tab.path}
            className={classes}
            data-testid={`app-sidebar-${tab.key}`}
            {...ariaProps}
          >
            {inner}
          </Link>
        );
      })}
    </nav>
  );
}

export default AppSidebar;
